<template>
  <nav
    v-if="prevUrl || nextUrl"
    class="flex flex-wrap items-center justify-between px-4 py-4 mt-auto md:flex-row"
    aria-label="Table navigation"
  >
    <div class="flex items-center gap-2">
      <span
        v-if="showLength"
        class="block w-full mb-4 text-sm font-normal text-grey-500 dark:text-theme-secondary md:mb-0 md:inline md:w-auto"
      >
        {{ $t('Showing') }}
        <span class="font-semibold text-theme-primary">
          {{ members?.from }}-{{ members?.to }}
        </span>
        {{ $t('of') }}
        <span class="font-semibold text-theme-primary">{{ members?.total }}</span>
      </span>
    </div>
    <ul class="inline-flex h-8 mx-auto -space-x-px text-sm rtl:space-x-reverse">
      <!-- Previous -->
      <li v-if="prevUrl">
        <Link
          :href="prevUrl + paramsUrl"
          class="flex items-center justify-center h-8 px-3 leading-tight text-grey-500 bg-white border border-grey-300 cursor-pointer ms-0 rounded-s-lg hover:bg-grey-100 hover:text-grey-700 dark:bg-grey-800 dark:border-grey-700 dark:text-theme-secondary dark:hover:bg-grey-700 dark:hover:text-white"
        >
          {{ $t('Previous') }}
        </Link>
      </li>

      <!-- Page numbers -->
      <template v-for="(item, index) in visiblePages" :key="index">
        <li v-if="item.ellipsis">
          <span class="flex items-center justify-center h-8 px-3 leading-tight text-grey-500 bg-white border border-grey-300 dark:bg-grey-800 dark:border-grey-700 dark:text-theme-disabled">
            &hellip;
          </span>
        </li>
        <li v-else-if="item.active">
          <Link
            :href="item.url + paramsUrl"
            aria-current="page"
            :class="{
              'rounded-s-lg': !prevUrl && index === 0,
              'rounded-e-lg': !nextUrl && index === visiblePages.length - 1,
            }"
            class="flex items-center justify-center h-8 px-3 transition-all border border-grey-300 cursor-pointer text-main-dark bg-main-lighter hover:bg-main hover:text-main-lighter dark:border-grey-700 dark:bg-grey-700 dark:text-white dark:hover:bg-grey-600"
          >
            {{ item.label }}
          </Link>
        </li>
        <li v-else>
          <Link
            :href="item.url + paramsUrl"
            :class="{
              'rounded-s-lg': !prevUrl && index === 0,
              'rounded-e-lg': !nextUrl && index === visiblePages.length - 1,
            }"
            class="items-center justify-center hidden h-8 px-3 leading-tight transition-all bg-white border border-grey-300 cursor-pointer text-theme-secondary md:flex hover:bg-grey-100 hover:text-secondary-16 dark:bg-grey-800 dark:border-grey-700 dark:text-theme-secondary dark:hover:bg-grey-700 dark:hover:text-white"
          >
            {{ item.label }}
          </Link>
        </li>
      </template>

      <!-- Next -->
      <li v-if="nextUrl">
        <Link
          :href="nextUrl + paramsUrl"
          class="flex items-center justify-center h-8 px-3 leading-tight text-grey-500 bg-white border border-grey-300 cursor-pointer rounded-e-lg hover:bg-grey-100 hover:text-grey-700 dark:bg-grey-800 dark:border-grey-700 dark:text-theme-secondary dark:hover:bg-grey-700 dark:hover:text-white"
        >
          {{ $t('Next') }}
        </Link>
      </li>
    </ul>
  </nav>
</template>
<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
  members: Object,
  showLength: { type: Boolean, required: false },
  paramsUrl: { type: String, default: '' },
});

const isLengthAware = computed(() => !!props.members?.last_page);
const currentPage = computed(() => props.members?.current_page ?? 1);
const prevUrl = computed(() => props.members?.prev_page_url);
const nextUrl = computed(() => props.members?.next_page_url);

function getPageUrl(page) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', page.toString());
  return url.pathname + url.search;
}

// paginate() — derive windowed pages from last_page + current_page.
// We do NOT rely on links[] for page numbering because Laravel already
// inserts its own ellipsis entries (url: null) when page count is large,
// making i+1 !== page number. Instead we build the URL from the current
// location, falling back to any matching entry in links[].
const windowedLinks = computed(() => {
  if (!isLengthAware.value) return [];

  const total = props.members.last_page;
  const current = currentPage.value;

  // Build a map of page number → url from whatever Laravel gave us in links[]
  const urlMap = {};
  for (const link of props.members?.links ?? []) {
    const n = parseInt(link.label);
    if (link.url && !isNaN(n)) urlMap[n] = link.url;
  }
  const pageUrl = (page) => urlMap[page] ?? getPageUrl(page);

  if (total <= 1) return [];

  // Show all pages when total is small enough
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      label: String(i + 1),
      url: pageUrl(i + 1),
      active: i + 1 === current,
      ellipsis: false,
    }));
  }

  // Window of ±1 around current, clamped and expanded to size 3 at edges
  let winStart = Math.max(1, current - 1);
  let winEnd = Math.min(total, current + 1);
  if (winEnd - winStart < 2) {
    if (winStart === 1) winEnd = Math.min(total, 3);
    else winStart = Math.max(1, total - 2);
  }

  // Core visible set: first, last, window
  const visibleSet = new Set([1, total]);
  for (let p = winStart; p <= winEnd; p++) visibleSet.add(p);

  // Avoid hiding a single page behind an ellipsis
  const sorted = [...visibleSet].sort((a, b) => a - b);
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i + 1] - sorted[i] === 2) visibleSet.add(sorted[i] + 1);
  }

  const finalPages = [...visibleSet].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < finalPages.length; i++) {
    const page = finalPages[i];
    if (i > 0 && page - finalPages[i - 1] > 1) result.push({ ellipsis: true });
    result.push({ label: String(page), url: pageUrl(page), active: page === current, ellipsis: false });
  }
  return result;
});

// simplePaginate() — no last_page, build from current_page + prev/next signals
const simplePages = computed(() => {
  if (isLengthAware.value) return [];

  const current = currentPage.value;
  const result = [];

  result.push({ label: '1', url: getPageUrl(1), active: current === 1, ellipsis: false });

  if (current > 3) result.push({ ellipsis: true });

  if (current - 1 > 1) {
    result.push({ label: String(current - 1), url: getPageUrl(current - 1), active: false, ellipsis: false });
  }

  if (current > 1) {
    result.push({ label: String(current), url: getPageUrl(current), active: true, ellipsis: false });
  }

  if (nextUrl.value) {
    result.push({ label: String(current + 1), url: getPageUrl(current + 1), active: false, ellipsis: false });
  }

  return result;
});

const visiblePages = computed(() => isLengthAware.value ? windowedLinks.value : simplePages.value);
</script>
